import { useState } from 'react'
import { useDocumentos } from '../../api/queries/documentos'
import { useSessao } from '../../auth/contexto'
import { podeFiscalizar } from '../../auth/permissoes'
import { Botao, BotaoLink } from '../../components/Botao'
import { AvisoDeErro, EstadoVazio, Esqueleto } from '../../components/Estados'
import { IconeConversa, IconeDocumento, IconeEnviar } from '../../components/IconesDeDocumento'
import { TEXTOS } from '../../lib/glossario'
import type { DocumentoDoContrato } from '../../types/documentos'
import { CartaoDoDocumento } from './CartaoDoDocumento'
import { DialogoDeEnvio } from './DialogoDeEnvio'
import { DialogoDeRetirada } from './DialogoDeRetirada'

/**
 * Os documentos do contrato: a base que a consulta por IA lê.
 *
 * Em vigor em cima, na ordem do servidor (o TR e o contrato primeiro); os retirados embaixo, num
 * bloco fechado — eles continuam abrindo, porque respostas antigas citam as páginas deles, mas não
 * disputam atenção com o que está valendo.
 *
 * Depois de enviar, o atalho para perguntar aparece: é o que a pessoa quer fazer em seguida.
 *
 * @returns a página
 */
export function PaginaDocumentos() {
  const t = TEXTOS.documentos
  const { usuario } = useSessao()
  const fiscal = podeFiscalizar(usuario)
  const documentos = useDocumentos()
  const [enviando, setEnviando] = useState(false)
  const [retirando, setRetirando] = useState<DocumentoDoContrato | null>(null)
  const [recemEnviado, setRecemEnviado] = useState<DocumentoDoContrato | null>(null)

  const lista = documentos.data ?? []
  const emVigor = lista.filter((documento) => !documento.retiradoEm)
  const retirados = lista.filter((documento) => Boolean(documento.retiradoEm))
  const jaTemTr = emVigor.some((documento) => documento.tipo === 'TERMO_DE_REFERENCIA')

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="titulo-da-pagina">{t.titulo}</h1>
          <p className="mt-1 max-w-prose text-sm text-slate-600">{t.descricao}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <BotaoLink to="/consulta" aparencia="secundario">
            <IconeConversa className="h-4 w-4" />
            {TEXTOS.consulta.titulo}
          </BotaoLink>
          {fiscal && (
            <Botao aparencia="primario" onClick={() => setEnviando(true)}>
              <IconeEnviar className="h-4 w-4" />
              {t.enviar}
            </Botao>
          )}
        </div>
      </header>

      {recemEnviado && recemEnviado.leitura === 'LIDO' && (
        <div
          role="status"
          className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-900 ring-1 ring-inset ring-emerald-200"
        >
          <span>
            “{recemEnviado.titulo}” foi lido e já entra nas respostas da consulta.
          </span>
          <BotaoLink to="/consulta" tamanho="pequeno" aparencia="secundario">
            Fazer uma pergunta
          </BotaoLink>
        </div>
      )}

      {documentos.isLoading && <Esqueleto linhas={4} />}
      {documentos.isError && <AvisoDeErro erro={documentos.error} />}

      {documentos.isSuccess && emVigor.length === 0 && (
        <EstadoVazio
          icone={<IconeDocumento className="h-8 w-8" />}
          titulo={t.vazioTitulo}
          ajuda={fiscal ? t.vazioTexto : t.vazioSemPermissao}
          acao={
            fiscal ? (
              <Botao aparencia="primario" onClick={() => setEnviando(true)}>
                {t.enviar}
              </Botao>
            ) : undefined
          }
        />
      )}

      {emVigor.length > 0 && (
        <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl bg-white shadow-cartao ring-1 ring-slate-200/70">
          {emVigor.map((documento) => (
            <CartaoDoDocumento
              key={documento.id}
              documento={documento}
              podeRetirar={fiscal}
              aoRetirar={() => setRetirando(documento)}
            />
          ))}
        </ul>
      )}

      {retirados.length > 0 && (
        <details className="group rounded-xl bg-white/60 ring-1 ring-slate-200/70">
          <summary className="cursor-pointer select-none px-5 py-3 text-sm font-medium text-slate-700 marker:text-slate-400">
            {t.retirados} ({retirados.length})
          </summary>
          <ul className="divide-y divide-slate-100 border-t border-slate-100">
            {retirados.map((documento) => (
              <CartaoDoDocumento key={documento.id} documento={documento} podeRetirar={false} aoRetirar={() => undefined} />
            ))}
          </ul>
        </details>
      )}

      {enviando && (
        <DialogoDeEnvio
          jaTemTr={jaTemTr}
          aoFechar={() => setEnviando(false)}
          aoEnviar={(documento) => {
            setEnviando(false)
            setRecemEnviado(documento)
          }}
        />
      )}
      {retirando && <DialogoDeRetirada documento={retirando} aoFechar={() => setRetirando(null)} />}
    </div>
  )
}
